import { always, indexOf, length } from 'ramda';
import React, { createContext, FC, useCallback } from 'react';
import { BoardsOnboardingStep, Feature } from '../../../constants/Feature';
import { useDataCapsule } from '../useDataCapsule';
import { BOARDS_ONBOARDING_STEPS } from './FeatureOnboardingSteps';
import { createOnboardingFeatureStoreKey } from './FeatureOnboaring.utils';
import {
  IFeatureOnboarding,
  IFeatureOnboardingContext,
  IFeatureOnboardingStore,
  TOnboardingStep,
} from './useFeatureOnboarding.types';

const onboardingStepsMap: { [key in Feature]: TOnboardingStep[] } = {
  [Feature.Boards]: BOARDS_ONBOARDING_STEPS,
};

export const FeatureOnboardingContext = createContext<IFeatureOnboardingContext>({
  boardsFeatureOnboarding: {
    isStepActive: always(false),
    completeStep: always(null),
  },
});

export function useOnboardingFeatureRegister<S extends TOnboardingStep>(feature: Feature): IFeatureOnboarding<S> {
  const steps = onboardingStepsMap[feature];
  const storeKey = createOnboardingFeatureStoreKey(feature);

  const { value: store, set: setStore } = useDataCapsule<IFeatureOnboardingStore>(
    storeKey,
    { currentStep: 0, isCompleted: false },
    { currentStep: BOARDS_ONBOARDING_STEPS.length, isCompleted: true },
  );

  const completeStep = useCallback(
    (step: S) => {
      const nextStep = indexOf(step, steps) + 1;

      setStore({
        currentStep: nextStep,
        isCompleted: nextStep === length(steps),
      });
    },
    [store, setStore],
  );

  const isStepActive = useCallback(
    (step: S) => !store.isCompleted && indexOf(step, steps) >= store.currentStep,
    [store, setStore],
  );

  return {
    completeStep,
    isStepActive,
  };
}

export const FeatureOnboardingProvider: FC = ({ children }) => {
  const boardsFeatureOnboarding = useOnboardingFeatureRegister<BoardsOnboardingStep>(Feature.Boards);

  return (
    <FeatureOnboardingContext.Provider value={{ boardsFeatureOnboarding }}>
      {children}
    </FeatureOnboardingContext.Provider>
  );
};
